import { IconButton, Page, Tooltip } from '@dixa/lib-shared-habla';
import { User } from 'lib-shared-types-api';
import { useCallback, useState } from 'react';

import styles from './activityLog.module.scss';
import { ActivityLogDateRangeFilter } from './filters/components/activityLogDateRangeFilter';
import { EventTypeFilter } from './filters/components/eventTypeFilter';
import {
  UserActivityLogFeed,
  UserActivityLogFiltersValue,
} from './userActivityLogFeed';
import { userEventTypeLabels } from './userActivityTypes/filters/userEventTypeLabels';

type UserActivityLogProps = {
  /** The contact/user whose activities are displayed. */
  user: User;
};

/** Renders the "Activities" feed on a contact's details page. */
export function UserActivityLog({ user }: UserActivityLogProps): JSX.Element {
  const [filters, setFilters] = useState<UserActivityLogFiltersValue>({});
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleClearFilters = useCallback(() => setFilters({}), []);

  return (
    <Page.Section
      className={styles.section}
      title="Activities"
      controls={
        <>
          <ActivityLogDateRangeFilter
            fromTs={filters.fromTs}
            toTs={filters.toTs}
            onChange={(changed) =>
              setFilters((current) => ({ ...current, ...changed }))
            }
          />
          <EventTypeFilter
            labels={userEventTypeLabels}
            value={filters.eventTypes ?? []}
            onChange={(eventTypes) =>
              setFilters((current) => ({ ...current, eventTypes }))
            }
          />
          <Tooltip
            content={sortDirection === 'desc' ? 'Newest first' : 'Oldest first'}
          >
            <IconButton
              variant="tertiary"
              icon={sortDirection === 'asc' ? 'filter-z-a' : 'filter-a-z'}
              aria-label={
                sortDirection === 'desc'
                  ? 'Sorted newest first'
                  : 'Sorted oldest first'
              }
              onClick={() =>
                setSortDirection((current) =>
                  current === 'desc' ? 'asc' : 'desc',
                )
              }
            />
          </Tooltip>
        </>
      }
    >
      <div className={styles.root}>
        <UserActivityLogFeed
          user={user}
          filters={filters}
          sortDirection={sortDirection}
          onClearFilters={handleClearFilters}
        />
      </div>
    </Page.Section>
  );
}
